import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  Box,
  Chip,
  Grid,
  Paper,
  Stack,
  Alert
} from '@mui/material';
import { styled } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import HeatmapMap from '../../Heatmap/HeatmapMap';
import { generateGridPoints } from '../../../heatmap/lib/geolocation';

import logger from '../../../utils/logger';
// Styled Components
const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    borderRadius: '16px',
    maxWidth: '1000px',
    width: '95%',
    maxHeight: '95vh'
  }
}));

const StyledDialogTitle = styled(DialogTitle)(({ theme }) => ({
  background: 'linear-gradient(135deg, #0B91D6 0%, #0A7BC4 100%)',
  color: '#ffffff',
  padding: '24px',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center'
}));

const StyledDialogContent = styled(DialogContent)(({ theme }) => ({
  padding: '24px',
  backgroundColor: '#F8F9FA'
}));

const StatsCard = styled(Paper)(({ theme }) => ({
  padding: '20px',
  borderRadius: '12px',
  backgroundColor: '#FFFFFF',
  boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.08)',
  textAlign: 'center',
  transition: 'transform 0.2s',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.12)'
  }
}));

const StatLabel = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '13px',
  fontWeight: 500,
  color: '#666666',
  marginBottom: '8px'
}));

const StatValue = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '28px',
  fontWeight: 700,
  color: '#121927'
}));

const MapContainer = styled(Paper)(({ theme }) => ({
  padding: '24px',
  borderRadius: '12px',
  backgroundColor: '#FFFFFF',
  boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.08)',
  marginBottom: '20px'
}));

const LegendItem = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  padding: '8px 0'
}));

const LegendColor = styled(Box)(({ color }) => ({
  width: '32px',
  height: '32px',
  borderRadius: '50%',
  backgroundColor: color,
  border: '2px solid rgba(0,0,0,0.1)',
  boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)'
}));

const InfoChip = styled(Chip)(({ theme }) => ({
  backgroundColor: '#E3F2FD',
  color: '#0B91D6',
  fontWeight: 600,
  fontSize: '13px',
  padding: '4px 8px',
  height: 'auto'
}));

const GridDataModal = ({ open, onClose, heatmapData }) => {
  if (!heatmapData) {
    return null;
  }

  // Extract data with safe defaults
  const pins = heatmapData.pins || [];
  const results = heatmapData.results || []; // Actual API results with real lat/lng
  const businessName = heatmapData.businessName || 'Business';
  const keyword = heatmapData.keyword || 'Keyword';
  const gridSize = heatmapData.gridSize || Math.round(Math.sqrt(pins.length));
  const totalGridPoints = heatmapData.totalGridPoints || pins.length;
  const centerLocation = heatmapData.centerLocation || { lat: 30.7333, lng: 76.7794 };

  logger.log('[GridDataModal] Has real results data:', results.length > 0);
  logger.log('[GridDataModal] Results sample:', results.slice(0, 2));

  // Validate data
  if (!pins || pins.length === 0) {
    return (
      <StyledDialog open={open} onClose={onClose}>
        <StyledDialogTitle>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            No Data Available
          </Typography>
          <IconButton onClick={onClose} sx={{ color: '#ffffff' }}>
            <CloseIcon />
          </IconButton>
        </StyledDialogTitle>
        <StyledDialogContent>
          <Alert severity="warning">
            No heatmap data available to display. Please run a heatmap analysis first.
          </Alert>
        </StyledDialogContent>
      </StyledDialog>
    );
  }

  // Calculate statistics
  const greenCount = pins.filter(p => p.rank !== '20+' && typeof p.rank === 'number' && p.rank >= 1 && p.rank <= 3).length;
  const yellowCount = pins.filter(p => typeof p.rank === 'number' && p.rank > 3 && p.rank <= 7).length;
  const redCount = pins.filter(p => typeof p.rank === 'number' && p.rank > 7 && p.rank <= 20).length;
  const grayCount = pins.filter(p => p.rank === '20+' || p.rank === null || (typeof p.rank === 'number' && p.rank > 20)).length;

  // Calculate average rank
  const numericRanks = pins
    .map(p => p.rank)
    .filter(r => typeof r === 'number' && r >= 1 && r <= 20);
  
  const avgRank = numericRanks.length > 0 
    ? (numericRanks.reduce((sum, r) => sum + r, 0) / numericRanks.length).toFixed(1)
    : 'N/A';

  // USE THE SAME GRID GENERATION AS HEATMAP RESULTS PAGE
  // This ensures identical grid layout between both views
  logger.log('[GridDataModal] Generating grid points using geolocation library');
  logger.log('[GridDataModal] Center:', centerLocation, 'Radius:', heatmapData.radius || 2, 'Grid size:', gridSize);
  
  // Generate perfect grid points using the same function as HeatmapMapView
  const gridPoints = generateGridPoints(
    centerLocation, 
    heatmapData.radius || 2, // Use radius from heatmap data
    gridSize
  );
  
  logger.log('[GridDataModal] Generated', gridPoints.length, 'grid points');
  
  // Map pins to grid points with their ranks
  const mapResults = pins.map((pin, index) => {
    const gridPoint = gridPoints[index];
    
    if (!gridPoint) {
      logger.warn(`[GridDataModal] No grid point for pin ${index}`);
      return null;
    }
    
    // Convert rank
    let targetBusinessRank;
    if (pin.rank === '20+' || pin.rank === null) {
      targetBusinessRank = null;
    } else if (typeof pin.rank === 'number') {
      targetBusinessRank = pin.rank;
    } else {
      targetBusinessRank = null;
    }

    return {
      gridPoint: {
        id: gridPoint.id,
        lat: gridPoint.lat,
        lng: gridPoint.lng,
        address: `Grid ${gridPoint.id} - Rank: ${pin.rank || '20+'}`,
        gridX: gridPoint.gridX,
        gridY: gridPoint.gridY
      },
      targetBusinessRank: targetBusinessRank
    };
  }).filter(Boolean);

  // Calculate zoom level based on grid size
  let mapZoom;
  if (gridSize <= 3) {
    mapZoom = 13;
  } else if (gridSize <= 5) {
    mapZoom = 12;
  } else if (gridSize <= 7) {
    mapZoom = 11;
  } else {
    mapZoom = 10;
  }

  // Debug logging
  logger.log('[GridDataModal] ========== DEBUG INFO ==========');
  logger.log('[GridDataModal] Grid size:', gridSize, 'x', gridSize);
  logger.log('[GridDataModal] Total pins from data:', pins.length);
  logger.log('[GridDataModal] ✅ All', pins.length, 'grid points displayed in perfect grid layout');
  logger.log('[GridDataModal] Center location:', centerLocation);
  logger.log('[GridDataModal] Grid spacing: 1km (0.01 degrees)');
  logger.log('[GridDataModal] Zoom level:', mapZoom);
  logger.log('[GridDataModal] Stats:', { greenCount, yellowCount, redCount, grayCount });
  logger.log('[GridDataModal] Grid coordinates (should form perfect 5x5 pattern):');
  
  // Group pins by row for better visualization
  const pinsByRow = {};
  mapResults.forEach((r, i) => {
    const row = Math.floor(i / gridSize);
    if (!pinsByRow[row]) pinsByRow[row] = [];
    pinsByRow[row].push(`(${r.gridPoint.lat.toFixed(4)}, ${r.gridPoint.lng.toFixed(4)}) Rank:${r.targetBusinessRank || '20+'}`);
  });
  
  Object.keys(pinsByRow).forEach(row => {
    logger.log(`  Row ${row}:`, pinsByRow[row].join(' | '));
  });
  logger.log('[GridDataModal] ===============================');

  return (
    <StyledDialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <StyledDialogTitle>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5, display: 'flex', alignItems: 'center', gap: 1 }}>
            🗺️ Heatmap Grid Data
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.9 }}>
            {businessName} • "{keyword}"
          </Typography>
        </Box>
        <IconButton
          onClick={onClose}
          sx={{
            color: '#ffffff',
            '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' }
          }}
        >
          <CloseIcon />
        </IconButton>
      </StyledDialogTitle>

      <StyledDialogContent>
        {/* Statistics Cards */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6} sm={3}>
            <StatsCard>
              <StatLabel>🟢 Green (1-3)</StatLabel>
              <StatValue sx={{ color: '#34A853' }}>{greenCount}</StatValue>
              <Typography variant="caption" color="text.secondary">Excellent</Typography>
            </StatsCard>
          </Grid>
          <Grid item xs={6} sm={3}>
            <StatsCard>
              <StatLabel>🟡 Yellow (4-7)</StatLabel>
              <StatValue sx={{ color: '#FBBC05' }}>{yellowCount}</StatValue>
              <Typography variant="caption" color="text.secondary">Good</Typography>
            </StatsCard>
          </Grid>
          <Grid item xs={6} sm={3}>
            <StatsCard>
              <StatLabel>🔴 Red (8-20)</StatLabel>
              <StatValue sx={{ color: '#EA4335' }}>{redCount}</StatValue>
              <Typography variant="caption" color="text.secondary">Needs Work</Typography>
            </StatsCard>
          </Grid>
          <Grid item xs={6} sm={3}>
            <StatsCard>
              <StatLabel>⚪ Out of Top 20</StatLabel>
              <StatValue sx={{ color: '#9E9E9E' }}>{grayCount}</StatValue>
              <Typography variant="caption" color="text.secondary">Not Ranked</Typography>
            </StatsCard>
          </Grid>
        </Grid>

        {/* Quick Info */}
        <Stack direction="row" spacing={1} sx={{ mb: 3, flexWrap: 'wrap', gap: 1 }}>
          <InfoChip label={`Grid: ${gridSize} × ${gridSize}`} />
          <InfoChip label={`Total Points: ${totalGridPoints}`} />
          <InfoChip label={`Average Rank: ${avgRank}`} />
          <InfoChip label={`Visible Pins: ${mapResults.length}`} />
        </Stack>

        {/* Interactive Map */}
        <MapContainer>
          <Box sx={{ 
            borderRadius: '12px', 
            overflow: 'hidden', 
            boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.15)',
            border: '1px solid #E0E0E0'
          }}>
            <HeatmapMap 
              center={centerLocation}
              results={mapResults}
              height={500}
              zoom={mapZoom}
            />
          </Box>
          
          
        </MapContainer>
      </StyledDialogContent>
    </StyledDialog>
  );
};

export default GridDataModal;
